import { debounce } from "@solid-primitives/scheduled";
import type { StoredPrompt } from "@storytell/client";
import { StoredPromptsSyncRequestConfig, useStorytellClient } from "@storytell/client";
import { subscribeToGlobalEvent, subscribeToServerEvent } from "@storytell/events";
import { createResource, onCleanup, onMount, startTransition } from "solid-js";
import { isServer } from "solid-js/web";
import { createResourceStorage } from "../../lib/createResourceStorage";
import { createEntitySyncManager } from "../sync/createEntitySyncManager";

export type StoredPromptsService = ReturnType<typeof createStoredPromptsService>;

export const createStoredPromptsService = (deps: {
  authReady: () => boolean;
  authToken: () => string | undefined;
  userId: () => string | undefined;
  projectId: () => string | undefined;
}) => {
  const client = useStorytellClient();

  const storedPrompts = createEntitySyncManager<StoredPrompt>((prompt) => [prompt.name, prompt.description]);

  const storage = createResourceStorage(
    () => storedPrompts.all(),
    (value) => storedPrompts.hydrateFromSync(value || [])
  );

  // projectId is the reactive key: returning it gates the request on
  // X-Project-Id and triggers a refetch on project navigation.
  // The resource's value is consumed through `storage` below, not this binding.
  const [, { refetch }] = createResource(
    () => {
      if (isServer || !deps.authReady() || !deps.userId()) return false;
      return deps.projectId() ?? false;
    },
    async (): Promise<StoredPrompt[]> => {
      const request = StoredPromptsSyncRequestConfig.withBearerToken(deps.authToken());
      const response = await client.fetch(request, undefined, undefined);
      if (response.isErr()) {
        console.error("Failed to fetch stored prompts sync", response.error);
        return [];
      }

      const data = Object.values(response.value.prompts);
      if (isServer) {
        storage[1](data);
      }
      return data;
    },
    {
      initialValue: [] as StoredPrompt[],
      deferStream: true,
      storage: () => storage,
    }
  );

  // Debounced refetch function wrapped in startTransition
  const debouncedRefetch = debounce(() => {
    void startTransition(() => {
      void refetch();
    });
  }, 750);

  onMount(() => {
    const unsubscribeUserContext = subscribeToGlobalEvent("user-context-changed", () => {
      void refetch();
    });
    const unsubscribeRefresh = subscribeToGlobalEvent("refresh-sync", () => {
      void refetch();
    });
    const unsubscribeConnectivityRestored = subscribeToGlobalEvent("connectivity-restored", () => {
      void refetch();
    });
    const unsubscribeServerEvent = subscribeToServerEvent("cp.stored_prompts", () => {
      debouncedRefetch();
    });
    onCleanup(() => {
      unsubscribeUserContext();
      unsubscribeRefresh();
      unsubscribeConnectivityRestored();
      unsubscribeServerEvent();
      debouncedRefetch.clear();
    });
  });

  return {
    ...storedPrompts,
  };
};

// Helper functions for base64 encoding/decoding of prompt text
export const encodePromptText = (text: string): string =>
  btoa(Array.from(new TextEncoder().encode(text), (byte) => String.fromCharCode(byte)).join(""));
export const decodePromptText = (encoded: string): string =>
  new TextDecoder("utf-8", { fatal: true, ignoreBOM: true }).decode(
    Uint8Array.from(atob(encoded), (char) => char.charCodeAt(0))
  );
